/**
 * Active DRM sessions (GET /sessions) + revoke (DELETE /sessions/:id) on the
 * sig-control plane (controlApi, direct, JWT bearer). Tenant is IMPLICIT
 * (resolved server-side from the JWT org claim): the list + revoke are scoped to
 * the caller's OWNED streams, so no serviceId appears in the path. `serviceId` is
 * kept only to scope the react-query cache. Mirrors the control plane's Session.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { controlApi } from '@/api/client';
import { aesKeys } from './_shared';

/** One active session (metadata only — no key material). Mirrors Session. */
export interface AesSession {
  id: string;
  stream_id: string;
  kind?: string; // 'live' | 'vod'
  kid?: number;
  issued_at: number; // unix seconds
  expires_at: number; // unix seconds
  ip?: string;
  ua?: string;
}

export interface AesSessionsResponse {
  sessions: AesSession[];
  count: number;
}

export function useAesSessions(serviceId: string | undefined, refreshMs = 15_000) {
  return useQuery<AesSessionsResponse>({
    queryKey: aesKeys.sessions(serviceId ?? ''),
    enabled: !!serviceId,
    refetchInterval: refreshMs,
    staleTime: 5_000,
    retry: false,
    queryFn: async () =>
      (await controlApi.get<AesSessionsResponse>('/sessions')).data ?? {
        sessions: [],
        count: 0,
      },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, serviceId: string) {
  void qc.invalidateQueries({ queryKey: aesKeys.sessions(serviceId) });
  void qc.invalidateQueries({ queryKey: aesKeys.metrics(serviceId) });
}

/** Revoke one session by id; refreshes the session list + metrics on success. */
export function useRevokeAesSession(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => controlApi.delete(`/sessions/${encodeURIComponent(id)}`),
    onSuccess: () => invalidate(qc, serviceId),
  });
}

/**
 * Revoke many sessions at once (e.g. every session from one client IP+stream).
 * Runs the deletes concurrently; throws if any failed so the caller can surface it.
 */
export function useRevokeAesSessions(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => controlApi.delete(`/sessions/${encodeURIComponent(id)}`)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) throw new Error(`${failed}/${ids.length} revokes failed`);
      return ids.length;
    },
    onSuccess: () => invalidate(qc, serviceId),
  });
}
